import DynamicButtonLink from "@shared/components/DynamicButtonLink";
import { ArrowIcon } from "@shared/components/ui/Icon";
import React from "react";
import { EventMediaType } from "../../types/home.types";

export interface EventSectionProps {
  title?: string;
  subtitle?: string;
  description?: string;
  buttonText?: string;
  buttonLink?: string;
  buttonTarget?: string;
  imagePath?: string;
  videoPath?: string;
  mediaType?: EventMediaType;
  roundedImage?: boolean;
  videoAutoplay?: boolean;
  videoLoop?: boolean;
  videoMuted?: boolean;
}

const EventSection: React.FC<EventSectionProps> = ({
  title,
  subtitle,
  description,
  buttonText,
  buttonLink,
  buttonTarget = "_self",
  imagePath,
  videoPath,
  mediaType = "image",
  roundedImage = true,
  videoAutoplay = false,
  videoLoop = false,
  videoMuted = false,
}) => {
  const isVideo = mediaType === "video" && !!videoPath;

  if (isVideo) {
    return (
      <div className="flex flex-col col-span-full bg-white dark:bg-gray-800 shadow-sm rounded-xl overflow-hidden">
        {subtitle && (
          <header className="px-5 py-4 border-b border-gray-100 dark:border-gray-700/60">
            <h2 className="font-madefor font-semibold text-gray-800 dark:text-gray-100">
              {subtitle}
            </h2>
          </header>
        )}
        <div className="p-5 flex flex-col gap-4">
          <h2 className="text-5xl text-gray-800 dark:text-gray-100">{title}</h2>
          {description && (
            <p className="text-xl text-gray-600 dark:text-gray-400">
              {description}
            </p>
          )}
          <video
            src={videoPath}
            className="w-full rounded-xl object-cover aspect-video max-h-[480px]"
            autoPlay={videoAutoplay}
            loop={videoLoop}
            muted={videoMuted}
            playsInline
            controls={!videoAutoplay}
          />
          <DynamicButtonLink
            buttonText={buttonText}
            buttonLink={buttonLink}
            buttonTarget={buttonTarget}
            variant="link"
            icon={<ArrowIcon />}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col col-span-full bg-white dark:bg-gray-800 shadow-sm rounded-xl overflow-hidden">
      {subtitle && (
        <header className="px-5 py-4 border-b border-gray-100 dark:border-gray-700/60">
          <h2 className="font-madefor font-semibold text-gray-800 dark:text-gray-100">
            {subtitle}
          </h2>
        </header>
      )}
      <div className="flex flex-col md:flex-row">
        <div className="p-5 md:w-1/2 flex gap-4 flex-col items-start">
          <h2 className="text-5xl text-gray-800 dark:text-gray-100 mb-3">
            {title}
          </h2>
          {description && (
            <p className="text-xl text-gray-600 dark:text-gray-400 mb-6">
              {description}
            </p>
          )}
          <DynamicButtonLink
            buttonText={buttonText}
            buttonLink={buttonLink}
            buttonTarget={buttonTarget}
            variant="link"
            icon={<ArrowIcon />}
          />
        </div>
        <div className="md:w-1/2 relative">
          <div className="absolute bottom-0 right-0 p-2">
            <img src="/assets/images/home/badge-revise.png" alt="Badge" />
          </div>
          <img
            src={imagePath}
            alt="Event"
            className={`aspect-square w-full object-cover h-[499px] ${
              roundedImage ? "rounded-tl-full" : ""
            }`}
          />
        </div>
      </div>
    </div>
  );
};

export default EventSection;
